import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { graphqlQuery } from '@/services/graphql'
import apiClient from '@/api/client'
import { useOrganization } from '@/contexts/OrganizationContext'
import { useProfileSwitch } from '@/contexts/ProfileSwitchContext'
import { showSuccessToast, showErrorToast } from '@/api/errorHandler'
import { currencySymbol } from '@/utils/formatters'

/**
 * Pricing Strategy interface from backend
 */
export interface PricingStrategy {
  id: string
  name: string
  nameEn: string | null
  nameAr: string | null
  description: string | null
  descriptionEn: string | null
  descriptionAr: string | null
  basePrice: number
  currency: string
  taxRate: number
  strategyType: string
  pricingRules: string | null
  discountRules: string | null
  isActive: boolean
  isCurrent: boolean
  version: number
  versionDisplay: string
  expiryDate: string | null
  formattedPrice: string
  createdAt: string
  updatedAt: string
}

/**
 * Client Profile interface
 */
export interface ClientProfile {
  identificationNumber: string | null
  totalOrders: number
  orders: Array<{
    orderNumber: string
    acceptedAt: string | null
    providerName: string | null
    totalAmount: number
  }>
}

/**
 * Quotation interface
 */
export interface Quotation {
  orderId: string
  taxAmount: number
  totalAmount: number
}

/**
 * Order priority type
 */
export type OrderPriority = 'low' | 'normal' | 'high' | 'urgent' | 'emergency'

/**
 * Order status type
 */
export type OrderStatus =
  | 'draft'
  | 'submitted'
  | 'confirmed'
  | 'scheduled'
  | 'in_progress'
  | 'open'
  | 'completed'
  | 'cancelled'
  | 'failed_payment'
  | 'refunded'

/**
 * Appointment summary nested inside a company order
 */
export interface OrderAppointment {
  id: string
  status: string
  scheduledStart: string | null
  scheduledEnd: string | null
  label: string | null
  displayLabel: string | null
}

/**
 * Service summary nested inside a company order
 */
export interface OrderService {
  id: string
  status: string
  serviceName: string | null
  providerName: string | null
  providerPhone: string | null
}

/**
 * Order interface matching the GraphQL companyOrders schema
 */
export interface Order {
  id: string
  orderNumber: string
  status: OrderStatus | string
  statusDisplay: string | null
  priority: OrderPriority | string
  priorityDisplay: string | null

  // Client & Beneficiary
  clientName: string
  clientPhone: string | null
  clientEmail: string | null
  beneficiaryName: string | null
  beneficiaryPhone: string | null
  clientProfileId: string | null
  beneficiaryProfileId: string | null

  // Template & Category
  templateName: string | null
  categoryName: string | null
  categoryCode: string | null
  organizationName: string | null

  // Pricing
  totalAmount: string
  subtotal: string | null
  vatAmount: string | null
  taxRate: number | null
  currency: string | null
  providerEarningsGrossAmount: string | null
  providerEarningsCommission: string | null
  providerEarningsNet: string | null
  providerEarningsTax: string | null
  providerEarningsTotal: string | null
  providerEarningsCurrency: string | null

  // Counts
  totalServices: number
  totalAppointments: number
  completedServicesCount: number
  pendingServicesCount: number
  inProgressServicesCount: number
  servicesProgressPercentage: number

  // Dates
  createdAt: string
  updatedAt: string
  deadline: string | number | null
  nextAppointment: string | null
  lastAppointment: string | null
  daysUntilDeadline: number | null

  // Flags
  isOverdue: boolean
  canCancel: boolean
  canReschedule: boolean
  canSubmit: boolean
  canConfirm: boolean
  canRefund: boolean

  // Payment
  paymentStatus: string | null
  hasPayments: boolean

  // Nested
  appointments: OrderAppointment[]
  services: OrderService[]

  // Legacy fields kept for backward compatibility with existing UI
  workflowState: Record<string, unknown> | null
  clientProfile: ClientProfile | null
  pricingStrategy: PricingStrategy | null
  quotation: Quotation | null
  refundAmount: number | null
  refundReason: string | null
  refundedAt: string | null
  submittedAt: string | null
  confirmedAt: string | null
  completedAt: string | null
  requestedEndDate: string | null
  statusUpdatedAt: string | null
  statusVersion: number | null
  requirements: string | null
  specialInstructions: string | null
  templateSnapshot: string | null
}

/**
 * Order status options for filtering
 * Note: 'all' is used instead of empty string because Radix UI Select doesn't allow empty values
 */
export const ORDER_STATUSES: OrderStatus[] = [
  'draft',
  'submitted',
  'confirmed',
  'scheduled',
  'in_progress',
  'open',
  'completed',
  'cancelled',
  'failed_payment',
  'refunded',
]

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  confirmed: 'Confirmed',
  scheduled: 'Scheduled',
  in_progress: 'In Progress',
  open: 'Open',
  completed: 'Completed',
  cancelled: 'Cancelled',
  failed_payment: 'Failed Payment',
  refunded: 'Refunded',
}

export const ORDER_PRIORITY_LABELS: Record<OrderPriority, string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  urgent: 'Urgent',
  emergency: 'Emergency',
}

/**
 * Payment status options for filtering
 * Note: 'all' is used instead of empty string because Radix UI Select doesn't allow empty values
 */
export const PAYMENT_STATUSES = [
  { value: 'all', label: 'All Payment Status' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'partial', label: 'Partially Paid' },
  { value: 'paid', label: 'Paid' },
  { value: 'refunded', label: 'Refunded' },
] as const

/**
 * Filter options for orders query
 */
export interface OrderFilters {
  status?: string
  paymentStatus?: string
  search?: string
  dateFrom?: string
  dateTo?: string
  orderBy?: string
}

/**
 * GraphQL query for fetching orders scoped to the logged-in company.
 * Uses companyOrders instead of allOrders so only orders belonging to
 * the currently selected organization are returned.
 */
const COMPANY_ORDERS_QUERY = `
  query CompanyOrders(
    $companyId: ID!
    $status: String
    $dateFrom: Date
    $dateTo: Date
    $limit: Int
  ) {
    companyOrders(
      companyId: $companyId
      status: $status
      dateFrom: $dateFrom
      dateTo: $dateTo
      limit: $limit
    ) {
      id
      orderNumber
      status
      statusDisplay
      priority
      priorityDisplay
      createdAt
      updatedAt

      clientName
      clientPhone
      clientEmail
      beneficiaryName
      beneficiaryPhone
      clientProfileId
      beneficiaryProfileId

      templateName
      categoryName
      categoryCode
      organizationName

      totalAmount
      subtotal
      vatAmount
      taxRate
      currency
      providerEarningsGrossAmount
      providerEarningsCommission
      providerEarningsNet
      providerEarningsTax
      providerEarningsTotal
      providerEarningsCurrency

      totalServices
      totalAppointments
      completedServicesCount
      pendingServicesCount
      inProgressServicesCount
      servicesProgressPercentage

      isOverdue
      deadline
      nextAppointment
      lastAppointment
      daysUntilDeadline

      paymentStatus
      hasPayments

      canCancel
      canReschedule
      canSubmit
      canConfirm
      canRefund

      appointments {
        id
        status
        scheduledStart
        scheduledEnd
        label
        displayLabel
      }

      services {
        id
        status
        serviceName
        providerName
        providerPhone
      }
    }
  }
`

interface CompanyOrdersResponse {
  companyOrders: Order[]
}

interface MyOrdersResponse {
  myOrders: Order[]
}

const MY_ORDERS_QUERY = `
  query MyOrders(
    $status: String
    $limit: Int
  ) {
    myOrders(
      status: $status
      limit: $limit
    ) {
      id
      orderNumber
      status
      statusDisplay
      priority
      priorityDisplay
      createdAt
      updatedAt

      clientName
      clientPhone
      clientEmail
      beneficiaryName
      beneficiaryPhone
      clientProfileId
      beneficiaryProfileId

      templateName
      categoryName
      categoryCode
      organizationName

      totalAmount
      subtotal
      vatAmount
      taxRate
      currency
      providerEarningsGrossAmount
      providerEarningsCommission
      providerEarningsNet
      providerEarningsTax
      providerEarningsTotal
      providerEarningsCurrency

      totalServices
      totalAppointments
      completedServicesCount
      pendingServicesCount
      inProgressServicesCount
      servicesProgressPercentage

      isOverdue
      deadline
      nextAppointment
      lastAppointment
      daysUntilDeadline

      paymentStatus
      hasPayments

      canCancel
      canReschedule
      canSubmit
      canConfirm
      canRefund

      appointments {
        id
        status
        scheduledStart
        scheduledEnd
        label
        displayLabel
      }

      services {
        id
        status
        serviceName
        providerName
        providerPhone
      }
    }
  }
`

/**
 * ✅ Better: fetch single order directly by id
 * You already have `order(id)` in schema.
 * (If some fields here don't exist on OrderType, GraphQL will error → then remove those fields.)
 */
const ORDER_BY_ID_QUERY = `
  query GetOrder($id: ID!) {
    order(id: $id) {
      id
      orderNumber
      status
      workflowState
      computedStatus

      clientName
      beneficiaryName

      totalAmount
      providerEarningsGrossAmount
      providerEarningsCommission
      providerEarningsNet
      providerEarningsTax
      providerEarningsTotal
      providerEarningsCurrency
      refundAmount
      refundReason
      refundedAt

      totalServices
      totalAppointments

      createdAt
      updatedAt
      submittedAt
      confirmedAt
      completedAt
      requestedEndDate
      statusUpdatedAt
      statusVersion

      requirements
      specialInstructions
      templateSnapshot

      pricingStrategy {
        id
        name
        nameEn
        nameAr
        description
        descriptionEn
        descriptionAr
        basePrice
        currency
        taxRate
        strategyType
        pricingRules
        discountRules
        isActive
        isCurrent
        version
        versionDisplay
        expiryDate
        formattedPrice
        createdAt
        updatedAt
      }

      clientProfile {
        identificationNumber
        totalOrders
        orders(limit: 5) {
          orderNumber
          acceptedAt
          providerName
          totalAmount
        }
      }

      quotation {
        orderId
        taxAmount
        totalAmount
      }
    }
  }
`

interface OrderByIdResponse {
  order: Order | null
}

/**
 * Hook to fetch orders scoped to the currently selected organization.
 * Uses companyOrders query — returns only orders belonging to the logged-in company.
 *
 * Server-side filtering: status, dateFrom, dateTo (via companyOrders args)
 * Client-side filtering: search (orderNumber / clientName / beneficiaryName)
 */
export function useOrders(filters: OrderFilters = {}) {
  const { currentOrganization } = useOrganization()
  const { isBusinessClientMode } = useProfileSwitch()

  const query = useQuery({
    queryKey: ['orders', isBusinessClientMode ? 'my-orders' : currentOrganization?.id, filters],
    queryFn: async () => {
      if (isBusinessClientMode) {
        const variables: {
          status?: string | null
          limit?: number
        } = {
          status: filters.status && filters.status !== 'all' ? filters.status : null,
          limit: 100,
        }

        const response = await graphqlQuery<MyOrdersResponse>(MY_ORDERS_QUERY, variables)

        if (!response.data?.myOrders) {
          throw new Error('Failed to fetch business client orders')
        }

        return response.data.myOrders
      }

      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }

      const variables: {
        companyId: string
        status?: string | null
        dateFrom?: string | null
        dateTo?: string | null
        limit?: number
      } = {
        companyId: currentOrganization.id,
        status: filters.status && filters.status !== 'all' ? filters.status : null,
        dateFrom: filters.dateFrom ?? null,
        dateTo: filters.dateTo ?? null,
        limit: 100,
      }

      const response = await graphqlQuery<CompanyOrdersResponse>(COMPANY_ORDERS_QUERY, variables)

      if (!response.data?.companyOrders) {
        throw new Error('Failed to fetch orders')
      }

      return response.data.companyOrders
    },
    enabled: isBusinessClientMode || !!currentOrganization?.id,
    staleTime: 1000 * 60 * 2,
  })

  const loadedOrders = query.data ?? []

  // Client-side search across already-loaded orders
  const searchValue = (filters.search ?? '').trim().toLowerCase()
  const orders = !searchValue
    ? loadedOrders
    : loadedOrders.filter((o) => {
        return (
          (o.orderNumber ?? '').toLowerCase().includes(searchValue) ||
          (o.clientName ?? '').toLowerCase().includes(searchValue) ||
          (o.beneficiaryName ?? '').toLowerCase().includes(searchValue)
        )
      })

  const totalCount = orders.length

  return {
    orders,
    totalCount,
    isLoading: query.isLoading,
    isFetchingNextPage: false,
    isError: query.isError,
    error: query.error,
    hasNextPage: false,
    fetchNextPage: async () => {},
    refetch: query.refetch,
  }
}

/**
 * Hook to fetch a single order by ID
 */
export function useOrder(orderId: string | null) {
  return useQuery({
    queryKey: ['order', orderId],
    queryFn: async () => {
      if (!orderId) return null

      const response = await graphqlQuery<OrderByIdResponse>(ORDER_BY_ID_QUERY, { id: orderId })
      return response.data?.order ?? null
    },
    enabled: !!orderId,
    staleTime: 1000 * 60 * 5,
  })
}

/**
 * Hook to accept an order
 */
export function useAcceptOrder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (orderId: string) => {
      const response = await apiClient.post(`/api/v1/orders/${orderId}/accept/`)
      return response.data
    },
    onSuccess: () => {
      showSuccessToast('Order accepted successfully')
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['dashboardStats'] })
    },
    onError: (error) => {
      showErrorToast(error as Error)
    },
  })
}

/**
 * Hook to confirm an order
 */
export function useConfirmOrder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ orderId, forceUnpaid }: { orderId: string; forceUnpaid?: boolean }) => {
      const response = await apiClient.post(`/api/v1/orders/${orderId}/confirm/`, {
        force_unpaid: forceUnpaid,
      })
      return response.data
    },
    onSuccess: () => {
      showSuccessToast('Order confirmed successfully')
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['dashboardStats'] })
    },
    onError: (error) => {
      showErrorToast(error as Error)
    },
  })
}

/**
 * Hook to cancel an order
 */
export function useCancelOrder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ orderId, reason }: { orderId: string; reason: string }) => {
      const response = await apiClient.post(`/api/v1/orders/${orderId}/cancel/`, {
        reason,
      })
      return response.data
    },
    onSuccess: () => {
      showSuccessToast('Order cancelled successfully')
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['order-details'] })
      queryClient.invalidateQueries({ queryKey: ['dashboardStats'] })
    },
    onError: (error) => {
      showErrorToast(error as Error)
    },
  })
}

/**
 * Hook to submit a draft order (draft → submitted)
 */
export function useSubmitOrder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (orderId: string) => {
      const response = await apiClient.post(`/api/v1/orders/${orderId}/submit/`)
      return response.data
    },
    onSuccess: () => {
      showSuccessToast('Order submitted successfully')
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['order'] })
      queryClient.invalidateQueries({ queryKey: ['dashboardStats'] })
    },
    onError: (error) => {
      showErrorToast(error as Error)
    },
  })
}

/**
 * Hook to schedule an order (confirmed → scheduled)
 */
export function useScheduleOrder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ orderId, scheduledStart }: { orderId: string; scheduledStart?: string }) => {
      const response = await apiClient.post(`/api/v1/orders/${orderId}/schedule/`, {
        scheduled_start: scheduledStart,
      })
      return response.data
    },
    onSuccess: () => {
      showSuccessToast('Order scheduled successfully')
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['order'] })
      queryClient.invalidateQueries({ queryKey: ['dashboardStats'] })
    },
    onError: (error) => {
      showErrorToast(error as Error)
    },
  })
}

/**
 * Hook to ops-reject an order
 */
export function useOpsRejectOrder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ orderId, reason }: { orderId: string; reason: string }) => {
      const response = await apiClient.post(`/api/v1/orders/${orderId}/ops-reject/`, {
        reason,
      })
      return response.data
    },
    onSuccess: () => {
      showSuccessToast('Order rejected successfully')
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['order'] })
      queryClient.invalidateQueries({ queryKey: ['order-details'] })
      queryClient.invalidateQueries({ queryKey: ['dashboardStats'] })
    },
    onError: (error) => {
      showErrorToast(error as Error)
    },
  })
}

/**
 * Hook to refund an order
 */
export function useRefundOrder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ orderId, reason, amount }: { orderId: string; reason: string; amount?: number }) => {
      const response = await apiClient.post(`/api/v1/orders/${orderId}/refund/`, {
        reason,
        ...(amount && { amount }),
      })
      return response.data
    },
    onSuccess: () => {
      showSuccessToast('Order refunded successfully')
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['order'] })
      queryClient.invalidateQueries({ queryKey: ['order-details'] })
      queryClient.invalidateQueries({ queryKey: ['dashboardStats'] })
    },
    onError: (error) => {
      showErrorToast(error as Error)
    },
  })
}

/**
 * Hook to mark payment as failed
 */
export function useFailPaymentOrder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ orderId, reason }: { orderId: string; reason?: string }) => {
      const response = await apiClient.post(`/api/v1/orders/${orderId}/fail-payment/`, {
        reason,
      })
      return response.data
    },
    onSuccess: () => {
      showSuccessToast('Payment marked as failed')
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['order'] })
      queryClient.invalidateQueries({ queryKey: ['dashboardStats'] })
    },
    onError: (error) => {
      showErrorToast(error as Error)
    },
  })
}

/**
 * Hook to update an order (PATCH)
 */
export function useUpdateOrder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      orderId,
      data,
    }: {
      orderId: string
      data: {
        priority?: string
        special_instructions?: string
        requested_end_date?: string
        requirements?: Record<string, unknown>
        beneficiary_profile_id?: string
      }
    }) => {
      const response = await apiClient.patch(`/api/v1/orders/${orderId}/`, data)
      return response.data
    },
    onSuccess: () => {
      showSuccessToast('Order updated successfully')
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['order'] })
    },
    onError: (error) => {
      showErrorToast(error as Error)
    },
  })
}

/**
 * Status badge styles matching internal dashboard
 */
export const STATUS_STYLES: Record<string, string> = {
  draft: 'bg-yellow-100 text-yellow-800',
  submitted: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-blue-100 text-blue-800',
  scheduled: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-brand-primary/10 text-brand-primary',
  open: 'bg-brand-primary/10 text-brand-primary',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
  failed_payment: 'bg-red-100 text-red-800',
  refunded: 'bg-red-100 text-red-800',
}

/**
 * Priority badge styles matching internal dashboard
 */
export const PRIORITY_STYLES: Record<string, string> = {
  low: 'bg-gray-100 text-gray-800',
  normal: 'bg-gray-100 text-gray-800',
  high: 'bg-orange-100 text-orange-800',
  urgent: 'bg-red-100 text-red-800',
  emergency: 'bg-red-200 text-red-900',
}

/**
 * Get status badge color based on order status
 */
export function getStatusColor(status: string): string {
  return STATUS_STYLES[status?.toLowerCase()] || 'bg-gray-100 text-gray-800'
}

/**
 * Get priority badge color
 */
export function getPriorityColor(priority: string): string {
  return PRIORITY_STYLES[priority?.toLowerCase()] || 'bg-gray-100 text-gray-800'
}

/**
 * Get status display name
 */
export function getStatusDisplayName(status: string): string {
  return ORDER_STATUS_LABELS[status?.toLowerCase() as OrderStatus] || status || '-'
}

/**
 * Get priority display name
 */
export function getPriorityDisplayName(priority: string): string {
  return ORDER_PRIORITY_LABELS[priority?.toLowerCase() as OrderPriority] || priority || '-'
}

/**
 * Get payment status badge color
 */
export function getPaymentStatusColor(status: string): string {
  const colors: Record<string, string> = {
    unpaid: 'bg-red-100 text-red-800',
    partial: 'bg-yellow-100 text-yellow-800',
    paid: 'bg-green-100 text-green-800',
    refunded: 'bg-pink-100 text-pink-800',
  }
  return colors[status.toLowerCase()] || colors.unpaid
}

/**
 * Format currency amount
 * Accepts both string and number amounts
 */
export function formatOrderAmount(amount: string | number, currency?: string, locale: 'en' | 'ar' = 'en'): string {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount
  if (isNaN(num)) return '-'
  const formatted = new Intl.NumberFormat(locale === 'ar' ? 'ar-SA' : 'en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num)
  return `${currencySymbol(currency || 'SAR')} ${formatted}`
}

/**
 * Format date for display
 * Handles both string dates and numeric timestamps.
 * Pass locale so dates show in Arabic when language is ar.
 */
export function formatOrderDate(dateValue: string | number | null | undefined, locale: 'en' | 'ar' = 'en'): string {
  if (dateValue === null || dateValue === undefined || dateValue === '') return 'N/A'
  
  let date: Date
  if (typeof dateValue === 'number') {
    // Handle numeric timestamp (seconds or milliseconds)
    date = new Date(dateValue < 10000000000 ? dateValue * 1000 : dateValue)
  } else {
    date = new Date(dateValue)
  }
  
  if (isNaN(date.getTime())) return 'N/A'
  
  const localeTag = locale === 'ar' ? 'ar-SA' : 'en-US'
  return date.toLocaleDateString(localeTag, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

/**
 * Format datetime for display
 * Handles both string dates and numeric timestamps.
 * Pass locale so dates show in Arabic when language is ar.
 */
export function formatOrderDateTime(dateValue: string | number | null | undefined, locale: 'en' | 'ar' = 'en'): string {
  if (dateValue === null || dateValue === undefined || dateValue === '') return 'N/A'
  
  let date: Date
  if (typeof dateValue === 'number') {
    // Handle numeric timestamp (seconds or milliseconds)
    date = new Date(dateValue < 10000000000 ? dateValue * 1000 : dateValue)
  } else {
    date = new Date(dateValue)
  }
  
  if (isNaN(date.getTime())) return 'N/A'
  
  const localeTag = locale === 'ar' ? 'ar-SA' : 'en-US'
  return date.toLocaleString(localeTag, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: locale !== 'ar',
  })
}
